import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  EvoPlayerConfig, NanoPlayerConfig, NanoPlayerStreamEntry,
  GenericPayloadType,
  PlayerCameraAngle,
} from '@ezugi/types';

export const VIDEO_EVO = 'Evo';
export const VIDEO_NANO = 'Nano';

type VideoSetPayload = GenericPayloadType;

// TODO: This can not be moved to types package
// because the types package doesn't have the boostrap dependency
type VideoPlayer = typeof VIDEO_EVO | typeof VIDEO_NANO;

export type VideoManifestErrorPayload = {
  error: {
    type: string,
    reason: string,
    hasPlayerFailed: boolean,
    description: string
  },
  logic: { currentPlayer: string },
  client: { timestamp: number, pointInTime: number },
  session: {
    videoSessionId: string,
    scenario: unknown,
    gameType: string
  },
  v: string,
  videoPlayer: VideoPlayer
}

type CdnListConfig = EvoPlayerConfig | NanoPlayerConfig;

const initialState = {
  isPlaying: null as boolean | null,
  destroyed: false,
  ready: false,
  fullWidth: false,
  loading: true,
  reconnect: 0,
  cdnList: undefined as CdnListConfig | undefined,
  pause: null as null | Record<string, unknown>,
  error: null as VideoManifestErrorPayload['error'] | null,
  angle: 'front' as PlayerCameraAngle,
  /**
   * camera angle inside the video stream; useful when inside the same stream, camera angle changes\
   * different than `angle` which points to a stream (when we have different video streams for different angles)
   */
  angleInStream: 'front' as PlayerCameraAngle,
  // selectedStreamQuality needs a default value of null in order to trigger ABR
  selectedStreamQuality: null as NanoPlayerStreamEntry | null,
  availableQualities: [] as string[],
  evoContainerMounted: false,
  nanoLoaded: false, // load <- ONLY NANO
  warning: {} as { message?: string },
  currentPlayer: 'default',
  getStreamsConfigLoaded: false,
  nanoPlayerPendingReconnect: false,
};

const videoSlice = createSlice({
  name: 'video',
  initialState,
  reducers: {
    request() {},
    requested() {},
    served() {},
    failure() {},
    success(state) {
      return {
        ...state,
        getStreamsConfigLoaded: true,
      };
    },
    load(state) {
      return {
        ...state,
        nanoLoaded: true,
      };
    },
    set(state, { payload }: PayloadAction<VideoSetPayload>) {
      return {
        ...state,
        ...payload,
      };
    },
    selectAngle(state, { payload: angle }: PayloadAction<PlayerCameraAngle>) {
      return {
        ...state,
        angle,
      };
    },
    selectAngleInStream(state, { payload: angleInStream }: PayloadAction<PlayerCameraAngle>) {
      return {
        ...state,
        angleInStream,
      };
    },
    ready(state) {
      return {
        ...state,
        ready: true,
        destroyed: false,
        reconnect: 0,
      };
    },
    analyticsInit() {},
    analyticsSuccess() {},
    play(state) {
      return {
        ...state,
        error: null,
        warning: {},
        loading: false,
        pause: null,
        playing: true,
        reconnect: 0,
      };
    },
    pause(state, { payload: { VideoPlayer: _VideoPlayer, ...payload } = {} }: PayloadAction<any>) {
      return {
        ...state,
        loading: false,
        pause: payload,
        playing: false,
      };
    },
    stop() {},
    buffer() {},
    error() {},
    analyticsError(state, { payload }: PayloadAction<VideoManifestErrorPayload>) {
      return {
        ...state,
        ...payload,
      };
    },
    destroy(state) {
      return {
        ...state,
        destroyed: true,
        ready: false,
        playing: false,
        reconnect: 1,
      };
    },
    reconnect(state) {
      return {
        ...state,
        reconnect: state.reconnect + 1,
      };
    },
    nanoMediaFailed(state) {
      return {
        ...state,
        nanoPlayerPendingReconnect: true,
      };
    },
    reconnectAfterNanoMediaFailed(state) {
      return {
        ...state,
        nanoPlayerPendingReconnect: false,
      };
    },
    disconnect(state) {
      return {
        ...state,
        reconnect: 0,
        playing: false,
      };
    },
    tokenExpired() {},
    refreshNanoToken(state, { payload }: PayloadAction<string>) {
      if (!state.cdnList) {
        return state;
      }

      const streamEntries = (state.cdnList as NanoPlayerConfig)?.[state.angle] ?? [];
      const updatedStreamEntries = streamEntries.map((streamEntry) => ({
        ...streamEntry,
        token: {
          value: payload,
        },
        h5live: {
          ...streamEntry.h5live,
          security: {
            ...streamEntry.h5live.security,
            jwtoken: payload,
          },
        },
      }));

      return {
        ...state,
        cdnList: {
          ...state.cdnList,
          [state.angle]: updatedStreamEntries,
        },
      };
    },

    shouldStop() {},
    resume() {},
    evoContainerMount(state) {
      return {
        ...state,
        evoContainerMounted: true,
      };
    },
  },
});

export const videoActions = videoSlice.actions;
export default videoSlice.reducer;
